import dayjs from 'dayjs';
import { Wallet } from 'lucide-react';

import { useAssignReceiptTransfer } from '@/api/receipt/use-assign-receipt-transfer.ts';
import type { ReceiptDto, TransferDto } from '@/api/types';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { formatPrice } from '@/helpers/formatters.ts';

type MatchingTransferRowProps = {
  receipt: ReceiptDto;
  transfer: TransferDto;
};

const MatchingTransferRow = ({ receipt, transfer }: MatchingTransferRowProps) => {
  const { mutate: assignTransfer, isPending } = useAssignReceiptTransfer(receipt.uuid);

  const priceDiffers = transfer.amount !== receipt.amount;
  const partyDiffers = transfer.party !== receipt.party && transfer.rawParty !== receipt.party;

  return (
    <div className="flex flex-col gap-3 p-3 border rounded-lg text-xs sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <div className="w-8 h-8 shrink-0 bg-muted rounded-full flex items-center justify-center">
          <Wallet className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="font-medium">{transfer.party || transfer.rawParty}</span>
            <Badge variant="secondary">{transfer.method}</Badge>
            {priceDiffers && <Badge variant="warning">Different price</Badge>}
            {partyDiffers && <Badge variant="warning">Different party</Badge>}
          </div>
          <span className="text-muted-foreground">{dayjs(transfer.date).format('DD.MM.YYYY')}</span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col sm:items-end">
          <span className="font-semibold">{formatPrice(transfer.amount)}</span>
          {priceDiffers && (
            <span className="text-muted-foreground">receipt: {formatPrice(receipt.amount)}</span>
          )}
        </div>
        <Button size="sm" disabled={isPending} onClick={() => assignTransfer(transfer.uuid)}>
          Assign
        </Button>
      </div>
    </div>
  );
};

export default MatchingTransferRow;
